import { Typography } from "@saviynt/design-system";
import { useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ScrollHint } from "../OnboardingParts";

interface Day0VideoSectionProps {
  sectionRef: (el: HTMLElement | null) => void;
  onScrollToSteps: () => void;
  scrollHintLabel: string;
}

const zumaAccent = {
  em: (chunks: React.ReactNode) => (
    <span className="Onboarding__zuma font-semibold">{chunks}</span>
  ),
};

/** Screen 2 — video overview with three-pillars text on the left. */
export function Day0VideoSection({
  sectionRef,
  onScrollToSteps,
  scrollHintLabel,
}: Day0VideoSectionProps) {
  const intl = useIntl();
  const pillarsRef = useRef<HTMLUListElement>(null);

  // Add `is-visible` once when the pillar list snaps into the viewport —
  // fires the staggered CSS animation. Disconnects after the first trigger.
  useEffect(() => {
    const el = pillarsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("is-visible");
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative flex h-full snap-start snap-always items-center justify-center overflow-hidden px-6 py-12"
    >
      {/* Two-column: text ~30% left, video ~70% right */}
      <div className="flex w-full max-w-[84rem] items-center gap-10 max-lg:flex-col max-lg:gap-6">
        {/* Left: header + text content — no card, no border */}
        <div className="flex w-[30%] flex-shrink-0 flex-col gap-6 max-lg:w-full">
          {/* Header sits directly above the text, left-aligned */}
          <div className="flex flex-col gap-2">
            <Typography
              kind="label"
              color="--color-foreground-green-subtle"
              className="uppercase !tracking-[0.14em]"
            >
              <FormattedMessage
                id="baPNCK"
                description="Subheading on the Zuma video overview screen"
                defaultMessage="A one-minute tour of Zuma"
              />
            </Typography>
            <Typography kind="h1" color="--color-foreground-neutral-bold">
              <FormattedMessage
                id="86sua5"
                description="Heading on the Zuma video overview screen"
                defaultMessage="See Zuma In Action"
              />
            </Typography>
          </div>

          <Typography
            kind="body1"
            color="--color-foreground-neutral-medium"
            className="!leading-relaxed"
          >
            <FormattedMessage
              id="kVqlH7"
              description="Intro paragraph on Zuma video screen listing the three pillars"
              defaultMessage="This short overview will walk you through the three pillars of the platform:"
            />
          </Typography>

          <ul
            ref={pillarsRef}
            className="Onboarding__pillars flex flex-col gap-5"
          >
            <li className="Onboarding__pillar">
              <Typography
                kind="body1"
                color="--color-foreground-neutral-bold"
                className="!leading-snug"
              >
                <FormattedMessage
                  id="upiFsj"
                  description="Zuma video screen pillar 1 — Discover"
                  defaultMessage="<em>Discover</em> AI agents, service accounts, and non-human identities and gain contextual insights"
                  values={zumaAccent}
                />
              </Typography>
            </li>
            <li className="Onboarding__pillar">
              <Typography
                kind="body1"
                color="--color-foreground-neutral-bold"
                className="!leading-snug"
              >
                <FormattedMessage
                  id="jY3zrS"
                  description="Zuma video screen pillar 2 — Protect"
                  defaultMessage="<em>Protect</em> your environment by blocking agent actions at run time that fall outside approved permissions and business intent."
                  values={zumaAccent}
                />
              </Typography>
            </li>
            <li className="Onboarding__pillar">
              <Typography
                kind="body1"
                color="--color-foreground-neutral-bold"
                className="!leading-snug"
              >
                <FormattedMessage
                  id="qJvKA0"
                  description="Zuma video screen pillar 3 — Govern"
                  defaultMessage="<em>Govern</em> AI identities with clear ownership and accountability"
                  values={zumaAccent}
                />
              </Typography>
            </li>
          </ul>
        </div>

        {/* Right: video — takes remaining ~70% */}
        <div className="Onboarding__videoFrame min-w-0 flex-1 rounded-2xl p-[0.735rem] max-lg:w-full">
          <video
            controls
            className="w-full rounded-xl"
            aria-label={intl.formatMessage({
              id: "fO/Xlt",
              description: "Accessible label for the Zuma trial overview video",
              defaultMessage: "Zuma platform overview video",
            })}
          >
            <source
              src="https://zuma-public-assets.saviyntcloud.com/videos/day0-welcome-v2.mp4"
              type="video/mp4"
            />
          </video>
        </div>
      </div>

      <ScrollHint label={scrollHintLabel} onClick={onScrollToSteps} />
    </section>
  );
}
